import { Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

const STYLE_PROPS = ['flex', 'height', 'min-height'] as const;

/**
 * Fixes iOS keyboard issues with flex-grow layouts inside ion-content.
 *
 * Problem: With KeyboardResize.Native the webview shrinks when the keyboard opens,
 * causing flex-grow containers (ion-grid, .group-grow) to collapse and hide inputs.
 * Ionic's built-in scroll assist cannot compensate because the layout itself is broken.
 *
 * Solution — three steps on every input focus:
 * 1. Collapse flex layouts — override flex-grow to 0 so content sizes to its natural height.
 * 2. Wait for the visual viewport resize (keyboard animation) to finish.
 * 3. Scroll the focused input into view at ~1/3 from the top of the visible area.
 *
 * On blur, all inline style overrides are removed to restore the original layout.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardScrollService {
  private modifiedElements: HTMLElement[] = [];

  private layoutGeneration = 0;
  private isInitialized = false;
  private pendingScrollTarget: HTMLElement | null = null;
  private restoreTimer: ReturnType<typeof setTimeout> | null = null;

  init(): void {
    if (this.isInitialized || !Capacitor.isNativePlatform()) {
      return;
    }

    this.isInitialized = true;

    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (!this.isInputElement(target)) {
        return;
      }

      if (this.restoreTimer) {
        clearTimeout(this.restoreTimer);
        this.restoreTimer = null;
      }

      this.collapseLayout(target);
      this.pendingScrollTarget = target;
    });

    document.addEventListener('focusout', () => {
      this.pendingScrollTarget = null;
      this.restoreTimer = setTimeout(() => {
        this.restoreTimer = null;
        this.restoreLayout();
      }, 0);
    });

    window.visualViewport?.addEventListener('resize', () => {
      if (this.pendingScrollTarget) {
        const target = this.pendingScrollTarget;
        this.pendingScrollTarget = null;
        this.scrollFocusedIntoView(target);
      }
    });
  }

  private isInputElement(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return INPUT_TAGS.has(element.tagName) || element.isContentEditable;
  }

  private collapseLayout(focusedElement: HTMLElement): void {
    const ionContent = this.findIonContent(focusedElement);
    if (!ionContent) {
      return;
    }

    // Restore previous mutations before re-collapsing (handles cross-ion-content tab)
    this.restoreLayout();

    ionContent.querySelectorAll<HTMLElement>('ion-grid').forEach((element) => {
      this.setImportant(element, 'flex', '0 0 auto');
      this.setImportant(element, 'min-height', 'auto');
    });

    ionContent.querySelectorAll<HTMLElement>('app-groups-renderer').forEach((element) => {
      this.setImportant(element, 'height', 'auto');
    });

    ionContent.querySelectorAll<HTMLElement>('.group-grow').forEach((element) => {
      this.setImportant(element, 'flex', '0 0 auto');
    });
  }

  private restoreLayout(): void {
    this.layoutGeneration++;

    for (const element of this.modifiedElements) {
      for (const prop of STYLE_PROPS) {
        element.style.removeProperty(prop);
      }
    }
    this.modifiedElements = [];
  }

  private scrollFocusedIntoView(target: HTMLElement): void {
    const focused = document.activeElement as HTMLElement | null;
    const element = focused && this.isInputElement(focused) ? focused : target;

    const ionContent = this.findIonContent(element);
    if (!ionContent) {
      return;
    }

    ionContent
      .getScrollElement()
      .then((scrollElement) => {
        const scrollRect = scrollElement.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Position input at ~1/3 from the top of visible scroll area
        const targetY = scrollRect.top + scrollRect.height / 3;
        const scrollBy = elementRect.top - targetY;

        ionContent.scrollToPoint(0, scrollElement.scrollTop + scrollBy, 300).catch(console.error);
      })
      .catch(console.error);
  }

  private setImportant(element: HTMLElement, prop: string, value: string): void {
    element.style.setProperty(prop, value, 'important');
    if (!this.modifiedElements.includes(element)) {
      this.modifiedElements.push(element);
    }
  }

  private findIonContent(element: HTMLElement): HTMLIonContentElement | null {
    let current = element as HTMLElement | null;
    while (current) {
      if (current.tagName === 'ION-CONTENT') {
        return current as HTMLIonContentElement;
      }
      const root = current.getRootNode();
      const host = root instanceof ShadowRoot ? (root.host as HTMLElement) : null;
      current = current.parentElement ?? host ?? null;
    }
    return null;
  }
}
